import { useState, useEffect } from "react";
import { appId } from "../firebase";
import { fetchSystemReloadRow } from "../lib/appBootstrapData";
import { supabase } from "../supabase";
import type { AppUser } from "../hooks/useHRData";

declare global {
  interface Window {
    __initialReloadId?: string;
  }
}

/**
 * Encapsulates the remaining realtime-adjacent setup that lives at the App
 * root: browser online/offline state and the admin-triggered force-reload
 * listener (polls `system_config.reload_flag`).
 *
 * The bulk of Supabase realtime subscriptions are handled by the centralized
 * realtimeStore; only these two root-level concerns remain here.
 *
 * @param {Object|null} user - The currently authenticated user object (used to
 *   scope the force-reload listener to the active tenant). May be null before
 *   the session resolves.
 * @returns {{ isOffline: boolean }} Network online/offline state.
 */
export function useAppRealtime(user: AppUser | null) {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  // Track browser online/offline transitions so the UI can surface a banner.
  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // --- SYSTEM FORCE RELOAD LISTENER ---
  useEffect(() => {
    // Instantly expose the last known reload ID on mount to avoid undefined diagnostics during async auth
    const storedId = sessionStorage.getItem("adapthub_last_reload_id");
    if (storedId) {
      window.__initialReloadId = storedId;
    }

    if (!appId || !user?.id) return;

    let initialLoad = true;
    const activeTenantId = user?.tenantId;

    const fetchReload = async () => {
      const { data: row } = await fetchSystemReloadRow(activeTenantId);
      if (row && row.reload_flag) {
        const currentReloadId = row.reload_flag;

        if (currentReloadId) {
          const lastStoredId = sessionStorage.getItem("adapthub_last_reload_id");

          if (initialLoad) {
            sessionStorage.setItem("adapthub_last_reload_id", currentReloadId);
            window.__initialReloadId = currentReloadId;
            initialLoad = false;

            if (lastStoredId && lastStoredId !== currentReloadId) {
              console.log("[AppRealtime] Pending reload detected from previous session. Refreshing...");
              window.location.reload();
            }
          } else if (lastStoredId !== currentReloadId) {
            console.log("[AppRealtime] Force reload request detected from administrator.");
            sessionStorage.setItem("adapthub_last_reload_id", currentReloadId);
            window.location.reload();
          }
        }
      }
    };
    fetchReload();

    // Realtime channel for admin-triggered force reloads. The mount fetch
    // above only covers page loads; this channel reaches ALREADY-OPEN windows
    // (e.g. staff PWAs left running for days) the moment the admin presses
    // "System neu laden" (upsert into system_config.reload_flag).
    // Page-reload is not a TanStack invalidation concern, so this channel
    // lives in its owning hook instead of the centralized realtimeStore.
    let reloadChannel = null;
    if (activeTenantId) {
      reloadChannel = supabase
        .channel(`force-reload-${activeTenantId}`)
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'system_config', filter: `tenant_id=eq.${activeTenantId}` },
          (payload) => {
            const row = (payload?.new ?? {}) as { reload_flag?: string };
            const nextId = row.reload_flag;
            if (!nextId) return; // unrelated system_config row
            const lastStoredId = sessionStorage.getItem("adapthub_last_reload_id");
            // No baseline yet (fresh session): store silently, don't reload —
            // mirrors the mount logic and prevents reload storms.
            if (lastStoredId && lastStoredId !== nextId) {
              console.log("[AppRealtime] Force reload request detected from administrator (realtime).");
              sessionStorage.setItem("adapthub_last_reload_id", nextId);
              window.location.reload();
            } else if (!lastStoredId) {
              sessionStorage.setItem("adapthub_last_reload_id", nextId);
            }
          }
        )
        .subscribe();
    }

    return () => {
      if (reloadChannel) {
        supabase.removeChannel(reloadChannel);
      }
    };
  }, [user?.id, user?.tenantId]);

  return { isOffline };
}
